import { Icon } from '@fluentui/react/lib/Icon';
import { createId } from '@paralleldrive/cuid2';
import * as moment from 'moment';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import { getNextDeadline } from './SearchResult/Share';

import Budgets from './Budgets';
import thousandSeparator from '../../../../utils/thousandSeparator';

interface ITopicInformationProps {
  topicMetadata: any;
  expectedContribution: string;
  indicativeNumberOfGrants: number;
}

interface ITopicInformationState {
  budgets_collapsed: boolean;
  deadlines_collapsed: boolean;
}

const TopicInformation: React.FC<ITopicInformationProps> = ({ topicMetadata, expectedContribution, indicativeNumberOfGrants }) => {
  const [state, setState] = React.useState<ITopicInformationState>({
    budgets_collapsed: true,
    deadlines_collapsed: true,
  });



  const { deadlines_collapsed } = state;

  const formatExpectedContribution = (contribution: string) => {
    if (!contribution || contribution === "") return "0";

    // Split by " - " to handle range format like "1000000 - 1000000"
    const parts = contribution.split(' - ');
    const formattedParts = parts.map(part => {
      const trimmed = part.trim();
      return isNaN(Number(trimmed)) ? part : thousandSeparator(trimmed);
    });

    if (formattedParts.length === 2 && formattedParts[0] === formattedParts[1]) {
      return formattedParts[0];
    }

    return formattedParts.join(' - ');
  };

  if (!topicMetadata) {
    return (
      <div>
        <ul className="topic-information">
          <li>
            <strong>Programme</strong>

            <p>
              <Skeleton height={12} />
            </p>
          </li>

          <li>
            <strong>Topic status</strong>

            <p>
              <Skeleton />
            </p>
          </li>

          <li>
            <strong>Call identifier</strong>

            <p>
              <Skeleton />
            </p>
          </li>

          <li>
            <strong>Call title</strong>

            <p>
              <Skeleton />
            </p>
          </li>

          <li>
            <strong>Publication date</strong>

            <p>
              <Skeleton />
            </p>
          </li>

          <li>
            <strong>Opening date</strong>

            <p>
              <Skeleton />
            </p>
          </li>
        </ul>
      </div>
    );
  }
  const nextDeadline =
    (topicMetadata.deadline_dates[0] !== 'N/A' && (topicMetadata.topic_status === 'Open' || topicMetadata.topic_status === 'Draft'))
      ? moment(getNextDeadline(topicMetadata.deadline_dates)).format('DD MMM YYYY')
      : topicMetadata.topic_status;

  // Fixing the call page feature: 
  // Generating the required call page url    
  const parts = topicMetadata.topic_url.split('/');
  const callIdentifier = parts[parts.length - 1].split('-').slice(0, -1).join('-').toUpperCase();
  const baseURL = 'https://ec.europa.eu/info/funding-tenders/opportunities/portal/screen/opportunities/topic-search';
  // Required call page url:
  const callPageUrl = `${baseURL}?tenders=false&programmePart=&callIdentifier=${callIdentifier}`;

  return (
    <div>
      <ul className="topic-information">
        <li>
          <strong>Programme</strong>
          {topicMetadata.call_programme_description} {topicMetadata.call_programme ? <>({topicMetadata.call_programme})</> : ""}
        </li>

        <li>
          <strong>Topic status</strong>

          {topicMetadata.topic_status}
        </li>

        <li>
          <strong>Call identifier</strong>

          {topicMetadata.call_identifier}
        </li>

        <li>
          <strong>Call title</strong>

          {topicMetadata.call_title}
        </li>

        <li>
          <strong>Publication date</strong>
          {topicMetadata.publication_date === 'N/A'
            ? 'N/A'
            : moment(topicMetadata.publication_date).format('DD MMM YYYY')}
        </li>

        <li>
          <strong>Opening date</strong>
          {topicMetadata.planned_opening_date === 'N/A'
            ? 'N/A'
            : moment(topicMetadata.planned_opening_date).format('DD MMM YYYY')}
        </li>

        <li>
          <React.Fragment>
            <button
              className="topic-info-drop-btn"
              onClick={(e) => {
                e.preventDefault();
                setState({
                  budgets_collapsed: state.budgets_collapsed,
                  deadlines_collapsed: !state.deadlines_collapsed,
                });
              }}
            >
              <strong>
                Next deadline: {nextDeadline}{' '}
                {deadlines_collapsed ? <Icon iconName="ChevronDown" /> : <Icon iconName="ChevronUp" />}
              </strong>
            </button>

            <ul>{deadlines_collapsed ? '' : <Deadlines deadlines={topicMetadata.deadline_dates} />}</ul>
          </React.Fragment>
        </li>

        <li>
          <Budgets budget_groups={topicMetadata.budget_groups} />
        </li>

        <li>
          <strong>Expected contribution</strong>
          {formatExpectedContribution(expectedContribution)}
        </li>

        <li>
          <strong>Indicative number of grants</strong>
          {(indicativeNumberOfGrants !== null && indicativeNumberOfGrants !== 0) ? indicativeNumberOfGrants : "N/A"}
        </li>

        <li>
          {
            topicMetadata.trl_text !== "" ? (<><strong>TRL</strong>{topicMetadata.trl_text}</>) : <></>
          }
        </li>

        {topicMetadata.topic_url || topicMetadata.call_url ? (
          <li>
            <strong>External links:</strong>
            <div className="external-links">
              {topicMetadata.topic_url ? (
                <a className="action-button" target="_blank" rel="noreferrer" href={topicMetadata.topic_url}>
                  Topic page
                </a>
              ) : null}

              {topicMetadata.call_url ? (
                <a className="action-button" target="_blank" rel="noreferrer" href={callPageUrl}>
                  Call page
                </a>
              ) : null}
            </div>
          </li>
        ) : null}
      </ul>
    </div>
  );
};

export default TopicInformation;

interface IDeadlinesProps {
  deadlines: any[];
}

const Deadlines: React.FC<IDeadlinesProps> = ({ deadlines }) => {
  return (
    <React.Fragment>
      {deadlines.map((deadline: string) => {
        return <li key={createId()}>{formatDate(deadline)}</li>;
      })}
    </React.Fragment>
  );
};

const formatDate = (dateToFormat: string) => {
  if (dateToFormat === 'N/A') {
    return 'Ongoing';
  }
  return moment(dateToFormat).format('DD MMMM YYYY');
};
